import { useState } from "react";
import { Alert, Grid, Typography } from "@mui/material";
import dayjs from "dayjs";
import { toast } from "react-toastify";

import { BucketAwtENUM, EstadosOrdenTrabajoEnumChoice, useUploadFile } from "@/shared";
import { useUpdateOrdenTrabajo } from "@/shared/actions/erp";
import { CustomPaperBox } from "@/shared/components/ui";
import { ScrollableDialogProps } from "@/shared/components/ui/Dialogs";
import { useUiStore } from "@/store/ui";
import { OrdenTrabajo } from "@/shared/interfaces";
// Mismo uploader pulido que visitas (drag&drop + grilla) para estilo consistente.
import FotosVisitaMultiUploader from "../FotosVisitaMultiUploader";
import { FOTOS_FINALIZACION_REQUERIDAS, subirFotosTraslado } from "./fotos-traslado.utils";

// Traslados: exactamente 6 fotos de finalización (mín = máx).
const MIN = FOTOS_FINALIZACION_REQUERIDAS;
const MAX = FOTOS_FINALIZACION_REQUERIDAS;

type FinalizarTrasladoFotosModalProps = {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  ordenTrabajo?: OrdenTrabajo;
};

/**
 * Modal para FINALIZAR el traslado: exige "Fotos de finalización" (mín 2, máx 10)
 * y marca la OT como REALIZADA. Persiste las fotos en `urls_fotos_salida` (array)
 * y `fecha_hora_fin_instalacion`. Reemplaza el flujo de entrada/salida clásico
 * SOLO para traslados.
 */
export default function FinalizarTrasladoFotosModal({
  open,
  onClose,
  onSuccess,
  ordenTrabajo,
}: FinalizarTrasladoFotosModalProps) {
  const [files, setFiles] = useState<File[]>([]);
  const [showError, setShowError] = useState(false);
  const setIsGlobalLoading = useUiStore((s) => s.setIsGlobalLoading);
  const { handleUploadFile } = useUploadFile();
  const updateOT = useUpdateOrdenTrabajo<any>({
    enableRedirect: false,
    customSuccessMessage: "Traslado finalizado correctamente",
  });

  const PATH_BUCKET = `${BucketAwtENUM.IMAGE_INSTALACION}/${ordenTrabajo?.id_orden_trabajo}`;

  const handleConfirm = async () => {
    if (files.length < MIN) {
      setShowError(true);
      return;
    }
    setIsGlobalLoading(true);
    try {
      const fotos = await subirFotosTraslado(files, PATH_BUCKET, handleUploadFile, "Finalización");
      // urls_fotos_salida DEBE ser un dict con `photosSalida`: el PUT de
      // OrdenTrabajo hace (urls_fotos_salida or {}).get("photosSalida") y con un
      // array revienta ('list' object has no attribute 'get'). Claves
      // fotoFinalizacion1..N con photoName/photoUrl (compatibles con los lectores).
      const photosSalida: Record<string, any> = {};
      fotos.forEach((f, i) => {
        photosSalida[`fotoFinalizacion${i + 1}`] = {
          photoUrl: f.photoUrl,
          photoName: f.photoname,
          photoId: f.photoId,
          fecha_hora: f.fecha_hora,
        };
      });
      await updateOT.mutateAsync({
        id: ordenTrabajo?.id_orden_trabajo!,
        data: {
          estado_orden_trabajo: EstadosOrdenTrabajoEnumChoice.REALIZADA,
          fecha_hora_fin_instalacion: dayjs(),
          urls_fotos_salida: { photosSalida },
        },
      });
      setFiles([]);
      setShowError(false);
      onClose();
      onSuccess();
    } catch (e) {
      console.error("Error al finalizar traslado", e);
      toast.error("Error al finalizar el traslado o subir las fotos.");
    } finally {
      setIsGlobalLoading(false);
    }
  };

  return (
    <ScrollableDialogProps
      open={open}
      title="Finalizar Traslado — Fotos de finalización"
      minWidth="60%"
      contentNode={
        <CustomPaperBox sxPaper={{ p: 3 }}>
          <Grid item xs={12}>
            <Alert severity="warning" variant="outlined" sx={{ mb: 2 }}>
              Al finalizar se marcará el traslado como <b>REALIZADA</b>. Sube{" "}
              <b>exactamente {MIN} fotos de finalización</b>.
            </Alert>
            <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
              Agregar fotos de la finalización del traslado
            </Typography>
            <FotosVisitaMultiUploader
              files={files}
              setFiles={setFiles}
              min={MIN}
              max={MAX}
              label="Fotos de finalización"
            />
            {showError && files.length < MIN && (
              <Typography color="error" fontWeight={700} sx={{ mt: 1 }}>
                Debes subir exactamente {MIN} fotos de finalización (llevas {files.length}).
              </Typography>
            )}
          </Grid>
        </CustomPaperBox>
      }
      onClose={onClose}
      onConfirm={handleConfirm}
      confirmTextBtn="Finalizar traslado"
      confirmVariantBtn="outlined"
    />
  );
}
